import {
  isObject,
  isArray,
  forIn,
  forEach,
  cloneDeep,
  isPlainObject,
  map,
  mapValues,
  reduce,
  filter,
  merge,
  pick,
  keys,
  zipObject,
  first,
  has,
  isEmpty,
} from 'lodash';
import FileSaver from 'file-saver';
import JSZip from 'jszip';
import { CSVStringfy } from './CsvAsync';
import JSONUtils from './JSONUtils';

function flattenObject(inObj) {
  if (!isObject(inObj)) {
    //if input is not an object return it without mods
    return inObj;
  }

  var outObj = isArray(inObj) ? [] : {};
  forIn(inObj, function (inItem, inKey) {
    var keyPrefix = isObject(inObj) ? inKey : '';

    var flatItem;
    var hasContent = !isEmpty(inItem);
    if (isPlainObject(inItem)) {
      if (hasContent) {
        //if prop value is an object we need to flatten it again
        flatItem = flattenObject(inItem);
        forIn(flatItem, function (v, k) {
          outObj[keyPrefix + '.' + k] = v;
        });
      }

      outObj[keyPrefix] = JSON.stringify(inItem);
    } else if (isArray(inItem)) {
      if (hasContent) {
        //but if the prop value is an array, we need to flatten each item in array
        var arrayWithFlatItems = map(inItem, (item, index) => {
          return isObject(item) ? flattenObject(item) : item;
        });

        flatItem = arrayWithFlatItems;
        if (isPlainObject(first(arrayWithFlatItems))) {
          //merge and reduce to one object if array items are objects
          flatItem = reduce(arrayWithFlatItems, function (res, curr) {
            return merge(res, curr);
          });
        }

        //flatItem is now an object or an array of items to be printed as is
        if (isPlainObject(flatItem)) {
          //if the final value (flatItem) is an object, create a new prop for each object property
          forIn(flatItem, function (v, k) {
            outObj[keyPrefix + '.[*].' + k] = v;
          });
        } else {
          outObj[keyPrefix] = flatItem;
        }
      }

      outObj[keyPrefix] = JSON.stringify(inItem);
    } else {
      //if object prop is not an array or object, print it as is
      outObj[keyPrefix] = inItem;
    }
  });

  return outObj;
}

function flattenSamples(samplesIn) {
  let attrs = has(samplesIn, 'rawMessage')
    ? flattenObject(cloneDeep(samplesIn.rawMessage))
    : flattenObject(cloneDeep(samplesIn));

  let meta = has(samplesIn, 'nexlaMetaData')
    ? flattenObject(cloneDeep(samplesIn.nexlaMetaData))
    : [];

  let error = has(samplesIn, 'error') ? samplesIn.error : null;

  let forkToDataset = has(samplesIn, 'forkToDataset') ? samplesIn.forkToDataset : null;

  return {
    attrs,
    meta,
    error,
    forkToDataset,
  };
}

function rorderSampleProperties(sample) {
  if (!sample) {
    return sample;
  }

  const { nexlaMetaData, rawMessage, ...rest } = sample;

  if (nexlaMetaData && rawMessage) {
    // move nexlaMetaData above rawMessage for displaying the code view of
    // samples with metadata
    return {
      nexlaMetaData,
      rawMessage,
      ...rest,
    };
  }

  return sample;
}

/**
 * schema = { input: [...], output: [...] }
 * samples = {
 *   input: { nested, flat }
 *   output: { nested, flat }
 * }
 * format = 'csv' | 'json'
 */
function downloadSamples(schema, samples, format, fileNamePrefix = '') {
  /**
   * fileContent = {input, output}
   */
  function download(fileContent, extension) {
    // if single file, don't zip
    const sampleKeys = keys(fileContent);

    if (sampleKeys.length === 1) {
      const blob = new Blob([fileContent[sampleKeys[0]]], {
        type: extension === 'xml' ? 'application/xml' : 'application/json',
      });
      FileSaver.saveAs(blob, `${fileNamePrefix}samples.${extension}`);
    } else {
      // if multiple files, zip
      const zip = new JSZip();
      forEach(fileContent, (content, key) =>
        zip.file(`${fileNamePrefix}${key}.${extension}`, content),
      );
      zip.generateAsync({ type: 'blob' }).then((content) => {
        const blob = new Blob([content], { type: 'application/zip' });
        FileSaver.saveAs(blob, `${fileNamePrefix}samples.zip`);
      });
    }
  }

  function getCSVContent(schema, flatSamples) {
    const columns = map(
      filter(schema, ({ __type }) => __type !== 'object' && __type !== 'array'),
      '__name',
    );
    const headerRow = zipObject(columns, columns);
    const processedSamples = map(flatSamples, ({ attrs }) => pick(attrs, columns));
    processedSamples.unshift(headerRow);
    return CSVStringfy(processedSamples);
  }

  let fileContent;

  switch (format) {
    case 'csv':
      const sampleKeys = keys(samples);
      fileContent = {};

      Promise.all(map(samples, ({ flat }, key) => getCSVContent(schema[key], flat))).then(
        (values) => {
          forEach(values, (value, index) => {
            fileContent[sampleKeys[index]] = value;
          });
          download(fileContent, format);
        },
      );
      break;

    default:
      fileContent = mapValues(samples, ({ nested }) =>
        JSONUtils.parseToFormat(
          map(nested, (s) => (has(s, 'rawMessage') ? s.rawMessage : s)),
          'application/json',
        ),
      );
      download(fileContent, format);
  }
}

var DataSamplesProcessor = {
  flattenSamples,
  downloadSamples,
  rorderSampleProperties,
};

export default DataSamplesProcessor;
